// ✅ Phase 5: SimpleModeApp — the non-technical customer's UI.
// Liquid Glass 3D, sky-blue, mobile-first. Four buttons + More:
//   💬 Chat (live test) · ⚙️ Manage by Chat · 🧾 Orders · ☰ More
// Everything the owner needs is either a tap or a chat message away.
// The Complete Dashboard stays one switch away (never removed).
import React, { useState, useEffect } from 'react';
import { MessageSquareText, Brain, BellRing, Menu } from 'lucide-react';
import DemoChat from './DemoChat';
import SetupWizard from './SetupWizard';

type Tab = 'chat' | 'manage' | 'orders' | 'more';

function authHeaders(token?: string): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const t = token || (typeof window !== 'undefined' ? localStorage.getItem('tenet_auth_token') : null);
  if (t) h['Authorization'] = t.startsWith('Bearer ') ? t : `Bearer ${t}`;
  return h;
}

function OrdersPanel({ token }: { token?: string }) {
  const [orders, setOrders] = useState<any[]>([]);
  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, r] = await Promise.all([
          fetch('/api/tenet/orders?limit=15', { headers: authHeaders(token), cache: 'no-store' }).then((x) => x.json()).catch(() => null),
          fetch('/api/tenet/reservations', { headers: authHeaders(token), cache: 'no-store' }).then((x) => x.json()).catch(() => null),
        ]);
        setOrders(o?.orders || o?.data || []);
        setReservations(r?.reservations || []);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusColor = (s: string) => {
    switch (String(s || '').toLowerCase()) {
      case 'delivered': case 'completed': case 'confirmed': return '#34D399';
      case 'cancelled': case 'no_show': return '#F87171';
      case 'pending': case 'new': return '#FBBF24';
      default: return '#7DD3FC';
    }
  };

  if (loading) return <div className="liquid-card-3d" style={{ padding: 24, color: '#A9C7E8' }}>loading…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="liquid-card-3d" style={{ padding: 18 }}>
        <div style={{ fontWeight: 800, color: '#E6F4FF', marginBottom: 12, fontSize: 15 }}>🧾 Orders ({orders.length})</div>
        {orders.length === 0 && <div style={{ color: '#A9C7E8', fontSize: 13 }}>තවම orders නෑ. Customer කෙනෙක් order කරපු ගමන් මෙතැන + WhatsApp එකට alert එකක් එනවා.</div>}
        {orders.map((o: any) => (
          <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(125,211,252,0.1)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#E6F4FF', fontSize: 13, fontWeight: 700 }}>{o.orderNumber || o.order_number || `#${o.id}`}</div>
              <div style={{ color: '#A9C7E8', fontSize: 11.5 }}>{o.customerName || o.customerPhone || ''} · {o.productName || ''}</div>
            </div>
            <span className="lg-chip" style={{ color: statusColor(o.status), borderColor: statusColor(o.status) }}>{o.status || 'pending'}</span>
          </div>
        ))}
      </div>

      <div className="liquid-card-3d" style={{ padding: 18 }}>
        <div style={{ fontWeight: 800, color: '#E6F4FF', marginBottom: 12, fontSize: 15 }}>🏨 Bookings ({reservations.length})</div>
        {reservations.length === 0 && <div style={{ color: '#A9C7E8', fontSize: 13 }}>තවම bookings නෑ.</div>}
        {reservations.map((r: any) => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(125,211,252,0.1)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#E6F4FF', fontSize: 13, fontWeight: 700 }}>{r.reservationRef}</div>
              <div style={{ color: '#A9C7E8', fontSize: 11.5 }}>{r.guestName} · {r.checkIn} → {r.checkOut}</div>
            </div>
            <span className="lg-chip" style={{ color: statusColor(r.status), borderColor: statusColor(r.status) }}>{r.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GapsPanel({ token }: { token?: string }) {
  const [gaps, setGaps] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [staged, setStaged] = useState<{ gapId: number; pendingId: number; previewText: string } | null>(null);

  const load = async () => {
    try {
      const j = await fetch('/api/tenet/knowledge-gaps?status=open', { headers: authHeaders(token), cache: 'no-store' }).then((x) => x.json());
      setGaps(j?.gaps || []);
    } catch { /* keep old list */ }
    setLoaded(true);
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) load();
  };

  const scan = async () => {
    setBusy(true);
    try {
      await fetch('/api/tenet/knowledge-gaps/scan', { method: 'POST', headers: authHeaders(token) });
      await load();
    } catch { /* ignore */ }
    setBusy(false);
  };

  const approve = async (id: number) => {
    setBusy(true);
    try {
      const j = await fetch(`/api/tenet/knowledge-gaps/${id}/approve`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify({}) }).then((x) => x.json());
      if (j?.success && j.pendingId) setStaged({ gapId: id, pendingId: j.pendingId, previewText: j.previewText });
    } catch { /* ignore */ }
    setBusy(false);
  };

  const confirmStaged = async (kind: 'confirm' | 'cancel') => {
    if (!staged) return;
    setBusy(true);
    try {
      await fetch(`/api/tenet/pending-actions/${staged.pendingId}/${kind}`, { method: 'POST', headers: authHeaders(token) });
      setStaged(null);
      await load();
    } catch { /* ignore */ }
    setBusy(false);
  };

  const reject = async (id: number) => {
    setBusy(true);
    try {
      await fetch(`/api/tenet/knowledge-gaps/${id}/reject`, { method: 'POST', headers: authHeaders(token) });
      await load();
    } catch { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="liquid-card-3d" style={{ padding: 18 }}>
      <div style={{ cursor: 'pointer' }} onClick={toggle}>
        <div style={{ fontWeight: 800, color: '#E6F4FF' }}>🧠 Knowledge Gaps {loaded && gaps.length > 0 ? `(${gaps.length})` : ''}</div>
        <div style={{ fontSize: 12.5, color: '#A9C7E8' }}>customer ලා අහපු දේවල් අතරින් Luna ට උත්තර දෙන්න බැරි වුණ ඒවා — ඔයා approve කරොත් විතරයි Luna ඉගෙන ගන්නේ</div>
      </div>
      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="lg-btn" style={{ padding: '8px 12px', fontSize: 12, alignSelf: 'flex-start' }} disabled={busy} onClick={scan}>
            {busy ? 'working…' : '🔍 දැන් scan කරන්න'}
          </button>
          {loaded && gaps.length === 0 && <div style={{ color: '#A9C7E8', fontSize: 12.5 }}>✅ දැනට open gaps නෑ — Luna හොඳටම handle කරලා තියෙනවා.</div>}
          {gaps.map((g) => (
            <div key={g.id} style={{ borderTop: '1px solid rgba(125,211,252,0.12)', paddingTop: 10 }}>
              <div style={{ color: '#E6F4FF', fontSize: 13, fontWeight: 700 }}>📌 {g.topic} <span style={{ color: '#7DD3FC', fontWeight: 500 }}>×{g.questionCount}</span></div>
              {(g.examples || []).length > 0 && <div style={{ color: '#A9C7E8', fontSize: 11.5, marginTop: 3 }}>❧ "{g.examples[0]}"</div>}
              <div style={{ color: '#A9C7E8', fontSize: 11.5, marginTop: 5, background: 'rgba(56,189,248,0.08)', borderRadius: 8, padding: '7px 9px' }}>
                යෝජනාව: {String(g.suggestedKnowledge || '').slice(0, 160)}{String(g.suggestedKnowledge || '').length > 160 ? '…' : ''}
              </div>
              {staged && staged.gapId === g.id ? (
                <div style={{ marginTop: 8, background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.35)', borderRadius: 10, padding: 10 }}>
                  <div style={{ color: '#FBBF24', fontSize: 11.5, fontWeight: 700 }}>📋 තවම save කරලා නෑ — confirm කරන්න:</div>
                  <div style={{ color: '#E6F4FF', fontSize: 11.5, marginTop: 5, whiteSpace: 'pre-wrap' }}>{staged.previewText}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="lg-btn lg-btn-primary" style={{ padding: '8px 14px', fontSize: 12 }} disabled={busy} onClick={() => confirmStaged('confirm')}>✅ ඔව්, add කරන්න</button>
                    <button className="lg-btn lg-btn-danger" style={{ padding: '8px 14px', fontSize: 12 }} disabled={busy} onClick={() => confirmStaged('cancel')}>❌ නෑ</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="lg-btn lg-btn-primary" style={{ padding: '7px 12px', fontSize: 11.5 }} disabled={busy} onClick={() => approve(g.id)}>✅ Luna ට දෙන්න</button>
                  <button className="lg-btn" style={{ padding: '7px 12px', fontSize: 11.5 }} disabled={busy} onClick={() => reject(g.id)}>🗑️ එපා</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SimpleModeApp({ token, onSwitchToComplete }: { token?: string; onSwitchToComplete: () => void }) {
  const [tab, setTab] = useState<Tab>('chat');
  const [onboardingDone, setOnboardingDone] = useState<boolean | null>(null);
  const [connStatus, setConnStatus] = useState<any>(null);
  const [simStatus, setSimStatus] = useState<any>(null);
  const [orderBadge, setOrderBadge] = useState(0);

  const refreshStatus = () => {
    // v6.13.67: the tour auto-shows ONLY for accounts that chose WhatsApp Agent Setup
    // (and only until complete). Existing/legacy tenants and 'other' users never get it.
    fetch('/api/tenet/onboarding/setup-choice', { headers: authHeaders(token), cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => {
        if (j?.choice !== 'whatsapp_agent') { setOnboardingDone(true); return; }
        // v6.13.69: the tour auto-shows EXACTLY ONCE per account. After the first
        // sight it is manual-only (More -> Setup Tour) - never on every login.
        let tourSeen = false;
        try { tourSeen = localStorage.getItem('tenet_tour_seen') === '1'; } catch { /* private mode */ }
        if (tourSeen) { setOnboardingDone(true); return; }
        return fetch('/api/tenet/onboarding/status', { headers: authHeaders(token), cache: 'no-store' })
          .then((r2) => r2.json())
          .then((j2) => {
            const done = j2?.state?.step === 'done';
            if (!done) { try { localStorage.setItem('tenet_tour_seen', '1'); } catch { /* ignore */ } }
            setOnboardingDone(done);
          })
          .catch(() => setOnboardingDone(true));
      })
      .catch(() => setOnboardingDone(true));
    fetch('/api/tenet/whatsapp/connection-status', { headers: authHeaders(token), cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => setConnStatus(j))
      .catch(() => {});
    fetch('/api/tenet/simulation/status', { headers: authHeaders(token), cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => setSimStatus(j))
      .catch(() => {});
    fetch('/api/tenet/orders?status=pending&limit=50', { headers: authHeaders(token), cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => {
        const list = j?.orders || j?.data || [];
        setOrderBadge(Array.isArray(list) ? list.length : 0);
      })
      .catch(() => {});
  };

  useEffect(() => { refreshStatus(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (onboardingDone === null) {
    return <div style={{ padding: 40, color: '#A9C7E8', textAlign: 'center' }}>loading…</div>;
  }

  if (onboardingDone === false) {
    return (
      <div className="lg-bg" style={{ minHeight: '100dvh', overflowY: 'auto' }}>
        <SetupWizard token={token} onFinished={() => { setOnboardingDone(true); refreshStatus(); }} />
      </div>
    );
  }

  const TABS: { key: Tab; icon: any; label: string }[] = [
    { key: 'chat', icon: MessageSquareText, label: 'Chat' },
    { key: 'manage', icon: Brain, label: 'Teach Luna' },
    { key: 'orders', icon: BellRing, label: 'Orders' },
    { key: 'more', icon: Menu, label: 'More' },
  ];

  return (
    <div className="lg-bg" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      {/* header */}
      <div className="liquid-card-3d" style={{ margin: '12px 12px 0', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 13, background: 'linear-gradient(140deg,#38BDF8,#2563EB)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, boxShadow: '0 8px 22px rgba(56,189,248,0.45)' }}>🌙</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="lg-glow-text" style={{ fontWeight: 900, fontSize: 16 }}>Luna</div>
          <div style={{ fontSize: 11, color: '#A9C7E8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {connStatus?.connected ? `WhatsApp ${connStatus.mode === 'official' ? '☁️' : '🔗'} connected` : 'WhatsApp connect කරලා නෑ — browser එකේ වැඩ කරන්න පුළුවන්'}
          </div>
        </div>
        <button className="lg-btn" style={{ padding: '8px 12px', fontSize: 11.5 }} onClick={onSwitchToComplete}>Complete ↔</button>
      </div>

      {/* content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 14, paddingBottom: 92 }}>
        {tab === 'chat' && (
          <div>
            <div style={{ fontSize: 13, color: '#A9C7E8', margin: '2px 2px 10px' }}>💬 customer කෙනෙක් වගේ chat කරලා ඔයාගේ agent එක test කරන්න.</div>
            <DemoChat token={token} />
          </div>
        )}
        {tab === 'manage' && (
          <div>
            <div style={{ fontSize: 13, color: '#A9C7E8', margin: '2px 2px 10px' }}>⚙️ මොනවා හරි වෙනස් කරන්න ඕනද? මෙතැන ලියන්න — "අලුත් product එකක් එකතු කරමු", "tone එක වෙනස් කරන්න". හැම දෙයක්ම confirmation එක්ක!</div>
            <DemoChat token={token} autoRun={false} />
          </div>
        )}
        {tab === 'orders' && <OrdersPanel token={token} />}
        {tab === 'more' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="liquid-card-3d" style={{ padding: 18, cursor: 'pointer' }} onClick={() => { try { localStorage.removeItem('tenet_tour_seen'); } catch { /* ignore */ } fetch('/api/tenet/onboarding/advance', { method: 'POST', headers: authHeaders(token), body: JSON.stringify({ step: 'reset' }) }).catch(() => {}); window.location.reload(); }}>
              <div style={{ fontWeight: 800, color: '#E6F4FF' }}>🧭 Setup Tour නැවත</div>
              <div style={{ fontSize: 12.5, color: '#A9C7E8' }}>business → knowledge → persona → products → media → live test</div>
            </div>
            <div className="liquid-card-3d" style={{ padding: 18, cursor: 'pointer' }} onClick={async () => {
              try {
                const r = await fetch('/api/tenet/onboarding/setup-choice', { method: 'POST', headers: authHeaders(token), body: JSON.stringify({ choice: 'whatsapp_agent' }) });
                const j = await r.json();
                alert(j?.message || 'WhatsApp Agent Setup ✅');
              } catch { alert('⚠️ setup එක run කරන්න බැරි වුණා'); }
            }}>
              <div style={{ fontWeight: 800, color: '#E6F4FF' }}>📱 WhatsApp Agent Setup</div>
              <div style={{ fontSize: 12.5, color: '#A9C7E8' }}>nodes auto-wire කරන්න / WhatsApp agent එක setup කරන්න — ඕන වේලාවක</div>
            </div>

            <div className="liquid-card-3d" style={{ padding: 18, cursor: 'pointer' }} onClick={() => window.location.reload()}>
              <div style={{ fontWeight: 800, color: '#E6F4FF' }}>📱 WhatsApp Connection</div>
              <div style={{ fontSize: 12.5, color: '#A9C7E8' }}>{connStatus?.report ? connStatus.report.split('\n').slice(0, 3).join(' · ') : 'status බලන්න (refresh)'}</div>
            </div>
            <div className="liquid-card-3d" style={{ padding: 18, cursor: 'pointer' }} onClick={() => window.location.reload()}>
              <div style={{ fontWeight: 800, color: '#E6F4FF' }}>🧪 Simulation</div>
              <div style={{ fontSize: 12.5, color: '#A9C7E8' }}>{simStatus ? `${simStatus.messageCount}/${simStatus.target} (${simStatus.percent}%) · real testing ${simStatus.realTestingEnabled ? 'ON ✅' : 'off'}` : 'refresh'}</div>
            </div>
            <GapsPanel token={token} />
            <div className="liquid-card-3d" style={{ padding: 18, cursor: 'pointer' }} onClick={onSwitchToComplete}>
              <div style={{ fontWeight: 800, color: '#E6F4FF' }}>⬛ Complete Dashboard</div>
              <div style={{ fontSize: 12.5, color: '#A9C7E8' }}>සියලුම advanced tools — ඕන වේලාවක switch වෙන්න පුළුවන්</div>
            </div>
          </div>
        )}
      </div>

      {/* Mint-style floating dock (cloned from kangavena-agent-mint BottomNavBar) */}
      <div className="fixed bottom-4 sm:bottom-6 left-3 right-3 max-w-md mx-auto z-50">
        <div className="liquid-dock-3d px-3 py-2 flex items-center justify-around">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`dock-tab ${active ? 'dock-tab-active' : 'dock-tab-inactive'}`}
              >
                <div className="relative">
                  <Icon
                    className={`w-5 h-5 transition-transform ${active ? 'stroke-[2.5] text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.7)]' : 'stroke-[1.8]'}`}
                  />
                  {t.key === 'orders' && orderBadge > 0 && (
                    <span className="dock-badge bg-rose-500">{orderBadge}</span>
                  )}
                </div>
                <span className={`text-[10px] tracking-tight ${active ? 'font-bold text-white' : 'font-medium'}`}>
                  {t.label}
                </span>
                {active && <span className="dock-tab-dot" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
